import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  listProductsPickerQuery,
  listProductItemsByProductQuery,
  getProductItemDetailForOrderQuery,
  getProductDetailForOrderQuery
} from "../../Graphql/querries/orderInventoryQuery";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";
import type {
  GetProductDetailForOrderResponse,
  GetProductItemDetailForOrderResponse,
  ListProductItemsByProductResponse,
  ListProductsPickerInput,
  ListProductsPickerResponse,
  ProductDetailRow,
  ProductItem,
  ProductPickerRow
} from "./types/orderInventory";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

export type * from "./types/orderInventory";

/**
 * useListProductsPicker
 * Fetch a paginated list of products for the order line-item picker.
 */
export const useListProductsPicker = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<ProductPickerRow[], Error, ListProductsPickerInput>({
    mutationKey: ["listProductsPicker"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching products picker list",
        buildLogContext(
          "order-inventory",
          "list-products-picker",
          userId,
          companyId,
          storeId,
          {
            filters: JSON.stringify(input ?? {})
          }
        )
      );

      try {
        const response: ListProductsPickerResponse = await fetchGraphQL(
          listProductsPickerQuery,
          { input },
          setAlert
        );

        const { products, code } = response.listProducts;

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch products picker list",
            buildLogContext(
              "order-inventory",
              "list-products-picker",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          throw new Error(`API Error: ${code}`);
        }

        // ❌ Logically broken success response
        if (!products) {
          captureAppException(
            "API returned OK but products array missing",
            new Error("API returned OK but products array missing"),
            buildLogContext(
              "order-inventory",
              "list-products-picker",
              userId,
              companyId,
              storeId
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          throw new Error("API returned OK but products array missing");
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Products picker list fetched successfully",
          buildLogContext(
            "order-inventory",
            "list-products-picker",
            userId,
            companyId,
            storeId,
            {
              productCount: products.length
            }
          )
        );

        return products;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch products picker list",
          error,
          buildLogContext(
            "order-inventory",
            "list-products-picker",
            userId,
            companyId,
            storeId,
            {
              filters: JSON.stringify(input ?? {})
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw error;
      }
    }
  });
};

/**
 * useListProductItemsByProduct
 * Fetch product items for a given product in the order picker.
 */
export const useListProductItemsByProduct = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<ProductItem[], Error, { productId: string }>({
    mutationKey: ["listProductItemsByProduct"],

    mutationFn: async ({ productId }) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching product items for order picker",
        buildLogContext(
          "order-inventory",
          "list-product-items-picker",
          userId,
          companyId,
          storeId,
          {
            productId
          }
        )
      );

      try {
        const response: ListProductItemsByProductResponse = await fetchGraphQL(
          listProductItemsByProductQuery,
          { input: { productId } },
          setAlert
        );

        const { items, code } = response.listProductItemsByProduct;

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch product items for order picker",
            buildLogContext(
              "order-inventory",
              "list-product-items-picker",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code),
                productId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          throw new Error(`API Error: ${code}`);
        }

        // ❌ Logically broken success response
        if (!items) {
          captureAppException(
            "API returned OK but items array missing",
            new Error("API returned OK but items array missing"),
            buildLogContext(
              "order-inventory",
              "list-product-items-picker",
              userId,
              companyId,
              storeId,
              {
                productId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          throw new Error("API returned OK but items array missing");
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Product items for order picker fetched successfully",
          buildLogContext(
            "order-inventory",
            "list-product-items-picker",
            userId,
            companyId,
            storeId,
            {
              productId,
              itemCount: items.length
            }
          )
        );

        return items;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch product items for order picker",
          error,
          buildLogContext(
            "order-inventory",
            "list-product-items-picker",
            userId,
            companyId,
            storeId,
            {
              productId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw error;
      }
    }
  });
};

/**
 * useGetProductItemDetailForOrder
 * Fetch a single product item's detail for order line editing.
 */
export const useGetProductItemDetailForOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<ProductItem | null, Error, { id: string }>({
    mutationKey: ["getProductItemDetailForOrder"],

    mutationFn: async ({ id }) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching product item detail for order",
        buildLogContext(
          "order-inventory",
          "get-product-item-detail-order",
          userId,
          companyId,
          storeId,
          {
            productItemId: id
          }
        )
      );

      try {
        const response: GetProductItemDetailForOrderResponse =
          await fetchGraphQL(
            getProductItemDetailForOrderQuery,
            { input: { id } },
            setAlert
          );

        const { item, code } = response.getProductItemDetail;

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch product item detail for order",
            buildLogContext(
              "order-inventory",
              "get-product-item-detail-order",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code),
                productItemId: id
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        // ❌ Logically broken success response
        if (!item) {
          captureAppException(
            "API returned OK but product item object missing",
            new Error("API returned OK but product item object missing"),
            buildLogContext(
              "order-inventory",
              "get-product-item-detail-order",
              userId,
              companyId,
              storeId,
              {
                productItemId: id
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Product item detail for order fetched successfully",
          buildLogContext(
            "order-inventory",
            "get-product-item-detail-order",
            userId,
            companyId,
            storeId,
            {
              productItemId: id
            }
          )
        );

        return item;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch product item detail for order",
          error,
          buildLogContext(
            "order-inventory",
            "get-product-item-detail-order",
            userId,
            companyId,
            storeId,
            {
              productItemId: id
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

/**
 * useGetProductDetailForOrder
 * Fetch a single product's detail for order line editing (sellingMode, baseUnit).
 */
export const useGetProductDetailForOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<ProductDetailRow | null, Error, { id: string }>({
    mutationKey: ["getProductDetailForOrder"],

    mutationFn: async ({ id }) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching product detail for order",
        buildLogContext(
          "order-inventory",
          "get-product-detail-order",
          userId,
          companyId,
          storeId,
          {
            productId: id
          }
        )
      );

      try {
        const response: GetProductDetailForOrderResponse = await fetchGraphQL(
          getProductDetailForOrderQuery,
          { input: { id } },
          setAlert
        );

        const { product, code } = response.getProductDetail;

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch product detail for order",
            buildLogContext(
              "order-inventory",
              "get-product-detail-order",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code),
                productId: id
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        // ❌ Logically broken success response
        if (!product) {
          captureAppException(
            "API returned OK but product object missing",
            new Error("API returned OK but product object missing"),
            buildLogContext(
              "order-inventory",
              "get-product-detail-order",
              userId,
              companyId,
              storeId,
              {
                productId: id
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Product detail for order fetched successfully",
          buildLogContext(
            "order-inventory",
            "get-product-detail-order",
            userId,
            companyId,
            storeId,
            {
              productId: id
            }
          )
        );

        return product;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch product detail for order",
          error,
          buildLogContext(
            "order-inventory",
            "get-product-detail-order",
            userId,
            companyId,
            storeId,
            {
              productId: id
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};
